import { serviceLogger } from '../utils/logger.js'

/**
 * Get the parent domain from document.referrer
 * @returns string - The parent domain or empty string if not available
 */
export function getParentDomain(): string {
  // TODO: Remove hardcoded domain - this is for testing only
  // return 'walla.co.il'
  
  try {
    if (!document.referrer) {
      return ''
    }
    
    const url = new URL(document.referrer)
    return url.hostname.replace(/^www\./, '').toLowerCase()
  } catch (error) {
    serviceLogger.warn('Failed to parse parent domain from referrer:', error)
    return ''
  }
}

/**
 * Get the favicon URL for a domain
 * @param domain - The domain to get favicon for
 * @returns string - Favicon URL
 */
export function getFaviconUrl(domain: string): string {
  const cleanDomain = domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .toLowerCase()
  
  return `https://${cleanDomain}/favicon.ico`
}

/**
 * Get logo URL for a domain using Brandfetch
 * @param domain - The domain to get logo for
 * @param width - Logo width (default: 32)
 * @param height - Logo height (default: 32)
 * @returns string - Logo URL
 */
export function getLogoUrl(domain: string, width = 32, height = 32): string {
  const cleanDomain = domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .toLowerCase()
  
  return `https://cdn.brandfetch.io/${cleanDomain}/w/${width}/h/${height}?c=1ids7QUtlu5tRk0dYKX`
  // return `https://cdn.brandfetch.io/calcalistech.com/w/${width}/h/${height}?c=1ids7QUtlu5tRk0dYKX`
}

/**
 * Get logo URL for the parent domain
 * @param width - Logo width (default: 32)
 * @param height - Logo height (default: 32)
 * @returns string | null - Logo URL or null if no parent domain
 */
export function getParentLogoUrl(width = 32, height = 32): string | null {
  const parentDomain = getParentDomain()
  if (!parentDomain) {
    return null
  }
  
  return getLogoUrl(parentDomain, width, height)
}

/**
 * Get favicon URL for the parent domain
 * @returns string | null - Favicon URL or null if no parent domain
 */
export function getParentFaviconUrl(): string | null {
  const parentDomain = getParentDomain()
  if (!parentDomain) {
    return null
  }
  
  return getFaviconUrl(parentDomain)
}

/**
 * Get project logo URL with fallback chain
 * @param projectIconUrl - The project's custom icon URL
 * @param domain - The domain to fallback to
 * @param width - Logo width (default: 32)
 * @param height - Logo height (default: 32)
 * @returns string | null - Logo URL or null if no options available
 */
export function getProjectLogoUrl(
  projectIconUrl: string | null,
  domain: string,
  width = 32,
  height = 32
): string | null {
  // First priority: Use project's custom icon
  if (projectIconUrl) {
    serviceLogger.debug('Using project custom icon', { projectIconUrl })
    return projectIconUrl
  }

  // Second priority: Try Brandfetch
  const brandfetchUrl = getLogoUrl(domain, width, height)
  serviceLogger.debug('Using Brandfetch logo', { domain, brandfetchUrl })
  return brandfetchUrl
}

/**
 * Get project logo URL for parent domain with fallback chain
 * @param projectIconUrl - The project's custom icon URL
 * @param width - Logo width (default: 32)
 * @param height - Logo height (default: 32)
 * @returns string | null - Logo URL or null if no options available
 */
export function getParentProjectLogoUrl(
  projectIconUrl: string | null,
  width = 32,
  height = 32
): string | null {
  const parentDomain = getParentDomain()
  if (!parentDomain) {
    return null
  }

  return getProjectLogoUrl(projectIconUrl, parentDomain, width, height)
}

/**
 * Handle logo image error by falling back to favicon
 * @param event - The error event from the image
 * @param fallbackElement - Optional fallback element to show if favicon also fails
 */
export function handleLogoError(
  event: Event, 
  fallbackElement?: HTMLElement
): void {
  const img = event.target as HTMLImageElement
  const faviconUrl = getParentFaviconUrl()
  
  if (faviconUrl && img.src !== faviconUrl) {
    serviceLogger.info('Logo failed, falling back to favicon')
    img.src = faviconUrl
  } else if (fallbackElement) {
    // If favicon also fails or no favicon available, show fallback element
    img.style.display = 'none'
    fallbackElement.style.display = 'flex'
  }
}

/**
 * Handle project logo image error with fallback chain
 * @param event - The error event from the image
 * @param projectIconUrl - The project's custom icon URL
 * @param domain - The domain to fallback to
 * @param fallbackElement - Optional fallback element to show if all options fail
 */
export function handleProjectLogoError(
  event: Event,
  projectIconUrl: string | null,
  domain: string,
  fallbackElement?: HTMLElement
): void {
  const img = event.target as HTMLImageElement
  
  // If we were using project icon, try Brandfetch
  if (projectIconUrl && img.src === projectIconUrl) {
    const brandfetchUrl = getLogoUrl(domain)
    serviceLogger.info('Project logo failed, falling back to Brandfetch')
    img.src = brandfetchUrl
    return
  }
  
  // If we were using Brandfetch, try favicon
  const faviconUrl = getFaviconUrl(domain)
  if (faviconUrl && img.src !== faviconUrl) {
    serviceLogger.info('Brandfetch logo failed, falling back to favicon')
    img.src = faviconUrl
    return
  }
  
  // If favicon also fails, show fallback element
  if (fallbackElement) {
    img.style.display = 'none'
    fallbackElement.style.display = 'flex'
  }
} 